import { FormEvent, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiGet, apiPatch, apiPost } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Activity, Stage, TaskRow, Warning, relTime, tierColor, gradeColor, tierGradeConflict } from '../lib/types';
import { Alert, Badge, Button, Loading } from '../components/ui';
import { ResearchPanel } from '../components/DeepResearch';

interface Detail {
  contact: any;
  company: any;
  timeline: Activity[];
  tasks: TaskRow[];
  warnings: Warning[];
}

const ACTIVITY_ICON: Record<string, string> = {
  call: '📞', email_sent: '✉️', email_reply: '↩️', walkin: '🚶', note: '📝',
  stage_change: '↗️', task_created: '✅', task_completed: '✔️', contact_created: '✨', assigned: '👤', score: '⭐', research: '🔎',
};

export default function ContactDetail() {
  const { id } = useParams();
  const { data, loading, error, reload } = useAsync(() => apiGet<Detail>(`/api/contacts/${id}`), [id]);
  const stages = useAsync(() => apiGet<{ stages: Stage[] }>('/api/stages'), []);
  const [note, setNote] = useState('');
  const [posting, setPosting] = useState(false);

  if (loading) return <Loading label="Loading contact…" />;
  if (error) return <Alert>{error}</Alert>;
  if (!data) return null;
  const c = data.contact;

  async function changeStage(stageId: string) {
    await apiPatch(`/api/contacts/${id}`, { stageId });
    reload();
  }
  async function addNote(e: FormEvent) {
    e.preventDefault();
    if (!note.trim()) return;
    setPosting(true);
    try {
      await apiPost(`/api/contacts/${id}/note`, { body: note });
      setNote('');
      reload();
    } finally {
      setPosting(false);
    }
  }

  return (
    <div className="space-y-6">
      <Link to="/contacts" className="text-sm text-muted hover:text-soft">← All contacts</Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="h-display text-3xl text-soft">{c.businessName || c.contactName}</h1>
          <p className="mt-1 text-sm text-muted">
            {[c.contactName && c.businessName ? c.contactName : null, c.title, data.company?.name].filter(Boolean).join(' · ') || '—'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {c.grade && <Badge className={`border-line ${gradeColor(c.grade)}`}>🔎 Grade {c.grade}</Badge>}
          {c.tier && <Badge className={`border-line ${tierColor(c.tier)}`}>{c.tier}{c.score != null ? ` · ${c.score}` : ''}</Badge>}
          <select
            value={c.stageId || ''}
            onChange={(e) => changeStage(e.target.value)}
            className="rounded-lg border border-line bg-surface-3 px-3 py-2 text-sm text-soft outline-none focus:border-power"
          >
            {(stages.data?.stages || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
      </div>

      {tierGradeConflict(c.tier, c.grade) && (
        <Alert kind="info">
          This contact was auto-scored <span className="font-semibold">{c.tier}</span> but Deep Research graded it{' '}
          <span className="font-semibold">{c.grade}</span>. The research grade is the deeper, evidence-based read — trust it over the quick score.
        </Alert>
      )}

      {data.warnings.length > 0 && (
        <div className="space-y-2">
          {data.warnings.map((w, i) => (
            <Alert key={i} kind={w.kind === 'warm_intro' ? 'success' : 'info'}>
              <span className="font-semibold">{w.kind === 'warm_intro' ? 'Warm intro: ' : 'Heads up: '}</span>{w.message}
            </Alert>
          ))}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        {/* Timeline */}
        <div className="space-y-4">
          <ResearchPanel contactId={Number(id)} onDone={reload} />

          <form onSubmit={addNote} className="card p-3">
            <textarea className="input min-h-[70px]" placeholder="Log a note… (calls, emails, and visits log themselves)" value={note} onChange={(e) => setNote(e.target.value)} />
            <div className="mt-2 flex justify-end"><Button type="submit" loading={posting} className="px-3 py-1.5 text-sm">Add note</Button></div>
          </form>

          <div>
            <h2 className="mb-2 font-head text-sm font-semibold uppercase tracking-wide text-muted">Activity</h2>
            {data.timeline.length === 0 ? (
              <div className="card px-4 py-6 text-center text-sm text-muted">Nothing logged yet. As you call, email, and visit, it shows up here — automatically.</div>
            ) : (
              <ol className="relative space-y-3 border-l border-line pl-5">
                {data.timeline.map((a) => (
                  <li key={a.id} className="relative">
                    <span className="absolute -left-[27px] flex h-6 w-6 items-center justify-center rounded-full border border-line bg-surface-2 text-xs">{ACTIVITY_ICON[a.type] || '•'}</span>
                    <div className="card px-3 py-2">
                      <div className="text-sm text-soft">{a.body || a.type}</div>
                      <div className="mt-0.5 text-xs text-muted">{a.userName || a.userEmail || 'System'} · {relTime(a.createdAt)}</div>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="card p-4">
            <h3 className="mb-3 font-head text-sm font-semibold uppercase tracking-wide text-muted">Details</h3>
            <dl className="space-y-2 text-sm">
              <Row k="Owner" v={c.ownerName} />
              <Row k="Email" v={c.email} />
              <Row k="Phone" v={c.phone} />
              <EditableCategory value={c.category} onSave={async (v) => { await apiPatch(`/api/contacts/${id}`, { category: v }); reload(); }} />
              <Row k="Deal value" v={c.dealValue != null ? `$${Number(c.dealValue).toLocaleString()}` : null} />
              <Row k="Last activity" v={relTime(c.lastActivityAt)} />
            </dl>
          </div>

          <div className="card p-4">
            <h3 className="mb-3 font-head text-sm font-semibold uppercase tracking-wide text-muted">Tasks</h3>
            <TaskList tasks={data.tasks} contactId={Number(id)} onChange={reload} />
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: any }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-muted">{k}</dt>
      <dd className="truncate text-right text-soft">{v || '—'}</dd>
    </div>
  );
}

// Category drives which per-industry research rubric a contact gets, so a mis-tag
// (Phase 8 Tier 2: realtors filed under "Professional Services") silently routes it
// to the wrong yardstick. Let the user fix it inline.
function EditableCategory({ value, onSave }: { value: string | null; onSave: (v: string) => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || '');
  const [busy, setBusy] = useState(false);
  async function save() {
    setBusy(true);
    try { await onSave(val.trim()); setEditing(false); } finally { setBusy(false); }
  }
  if (!editing) {
    return (
      <div className="flex justify-between gap-3">
        <dt className="text-muted">Category</dt>
        <dd className="truncate text-right text-soft">
          {value || '—'}
          <button onClick={() => { setVal(value || ''); setEditing(true); }} className="ml-2 text-xs font-semibold text-electric hover:underline">edit</button>
        </dd>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between gap-2">
      <dt className="text-muted">Category</dt>
      <dd className="flex items-center gap-1">
        <input autoFocus className="input py-1 text-sm" value={val} onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }} />
        <button onClick={save} disabled={busy} className="text-xs font-semibold text-success hover:underline disabled:opacity-50">save</button>
        <button onClick={() => setEditing(false)} className="text-xs text-muted hover:text-soft">✕</button>
      </dd>
    </div>
  );
}

function TaskList({ tasks, contactId, onChange }: { tasks: TaskRow[]; contactId: number; onChange: () => void }) {
  const [title, setTitle] = useState('');
  const open = tasks.filter((t) => t.status === 'open');

  async function add(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    await apiPost('/api/tasks', { contactId, title });
    setTitle('');
    onChange();
  }
  async function done(id: number) {
    await apiPatch(`/api/tasks/${id}`, { status: 'done' });
    onChange();
  }

  return (
    <div className="space-y-2">
      {open.length === 0 && <p className="text-xs text-muted">No open tasks.</p>}
      {open.map((t) => (
        <div key={t.id} className="flex items-start gap-2">
          <button onClick={() => done(t.id)} className="mt-0.5 h-4 w-4 shrink-0 rounded border border-line-strong hover:border-power" aria-label="Complete" />
          <div className="min-w-0 flex-1">
            <div className="text-sm text-soft">{t.title}</div>
            {t.autoGenerated === 1 && <span className="text-[10px] uppercase tracking-wide text-victory">auto</span>}
          </div>
        </div>
      ))}
      <form onSubmit={add} className="flex gap-2 pt-1">
        <input className="input py-1.5 text-sm" placeholder="Add a follow-up…" value={title} onChange={(e) => setTitle(e.target.value)} />
      </form>
    </div>
  );
}
